import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchAudit } from "@/modules/audit/presentation/api/client";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { kycRequestsApi } from "./kyc-client";

export const kycRequestKeys = {
  all: ["kyc-requests"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["kyc-requests", "list", q] as const,
  detail: (id: string) => ["kyc-requests", "detail", id] as const,
  audit: (id: string) => ["kyc-requests", "audit", id] as const,
};

export function useKycRequestListQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: kycRequestKeys.list(q),
    queryFn: () => kycRequestsApi.list(q),
  });
}

export function useKycRequestDetailQuery(id: string) {
  return useQuery({
    queryKey: kycRequestKeys.detail(id),
    queryFn: () => kycRequestsApi.getDetail(id),
    enabled: !!id,
  });
}

export function useApproveKycRequestMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => kycRequestsApi.approve(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kycRequestKeys.all });
      qc.invalidateQueries({ queryKey: kycRequestKeys.detail(id) });
      qc.invalidateQueries({ queryKey: kycRequestKeys.audit(id) });
      toast.success("ອະນຸມັດ KYC ສຳເລັດ");
    },
    onError: () => {
      toast.error("ການອະນຸມັດລົ້ມເຫຼວ");
    },
  });
}

export function useRejectKycRequestMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason?: string) => kycRequestsApi.reject(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kycRequestKeys.all });
      qc.invalidateQueries({ queryKey: kycRequestKeys.detail(id) });
      qc.invalidateQueries({ queryKey: kycRequestKeys.audit(id) });
      toast.success("ປະຕິເສດ KYC ສຳເລັດ");
    },
    onError: () => {
      toast.error("ການປະຕິເສດລົ້ມເຫຼວ");
    },
  });
}

export function useKycRequestAuditQuery(entityId: string) {
  return useQuery({
    queryKey: kycRequestKeys.audit(entityId),
    queryFn: () =>
      fetchAudit({
        limit: 50,
        offset: 0,
        sort: [{ field: "occurredAt", dir: "desc" }],
        filters: [
          { field: "entityType", op: "eq", value: "kyc_verification_request" },
          { field: "entityId", op: "eq", value: entityId },
        ],
      }),
    enabled: !!entityId,
  });
}
